import * as React from 'react';
import {Component, Plugins, withSystem, Atom} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  EvaluatedNodeData,
  getChildren,
  getStringOrElse,
  getInPojo,
} from '@packages/utilities/data-utils';
import {
  EBoolean,
  EElement,
  EList,
  ERecord,
  EText,
  TListOfElement,
  TRecord,
  TEnum,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {USYS_DATA_ATTRS} from '@packages/systems/users/constants';
import {
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {isUserComponentRoot} from '@packages/utilities/platform';

const UserAccountSubscriptionListWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, data, context, renderChild}) => {
    const uiState = getStringOrElse(
      getInPojo(data, ['temp', 'state']),
      'items'
    );
    const newContext = context.set('uiState', uiState);

    const props = generateAttributes(attributes, classes);

    return (
      <div {...props} {...{[USYS_DATA_ATTRS.userSubscriptions]: true}}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Subscriptions Wrapper',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Users'), iconName('AccountSubscriptionList')),
  },
  uiStateOnSelected: 'items',
  render: UserAccountSubscriptionListWrapper,
  showEmpty: true,
  constraints: {
    descendants: [
      {is: 'UserAccountSubscriptionList', rule: 'ExactlyOne'},
      {
        is: {
          check: (element, componentMap) =>
            isUserComponentRoot(componentMap, element),
          name: 'Symbol',
        },
        rule: 'Forbid',
      },
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
    ],
    ancestors: [
      {is: 'UserAccountWrapper', rule: 'ExactlyOne'},
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS.filter(
        (constraint) => constraint.is !== 'UserAccountWrapper'
      ),
    ],
    page: [{type: 'UtilityPage', is: 'usys-user-account'}],
  },
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    children: TListOfElement,
    temp: TRecord({
      state: withSystem(
        {
          label: 'State',
          defaultValue: 'items',
        },
        TEnum({
          items: 'Items',
          empty: 'Empty',
        })
      ),
    }),
  },
});

export default config;

export const userSubscriptionListPresetFactory = EElement({
  id: 'Subscriptions',
  type: qualify(
    Plugins.name('Users'),
    Component.name('UserAccountSubscriptionListWrapper')
  ),
  data: ERecord({
    temp: ERecord({
      state: EEnum('items'),
    }),
    children: EList([
      EElement({
        id: 'user-account-subscription-list',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserAccountSubscriptionList')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'user-account-subscription-item',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserAccountSubscriptionListItem')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Subscription Image',
                    type: qualify(
                      Plugins.name('Commerce'),
                      Component.name('CommerceCartItemImage')
                    ),
                    data: ERecord({
                      dyn: ERecord({
                        bind: ERecord({
                          src: EList([
                            ERecord({
                              slug: EText('variantImage'),
                              type: EText('ImageRef'),
                            }),
                          ]),
                        }),
                        condition: ERecord({
                          class: ERecord({
                            false: ERecord({
                              'w-condition-invisible': ERecord({
                                fields: ERecord({
                                  variantImage: ERecord({
                                    exists: EText('yes'),
                                  }),
                                }),
                              }),
                            }),
                          }),
                        }),
                      }),
                    }),
                  }),
                  EElement({
                    id: 'Subscription Item Column 1',
                    type: qualify(
                      Plugins.name('Users'),
                      Component.name('UserAccountSubscriptionListItemInfo')
                    ),
                    data: ERecord({
                      children: EList([
                        EElement({
                          id: 'Subscription Name',
                          type: qualify(
                            Plugins.name('Commerce'),
                            Component.name('CommerceCartProductName')
                          ),
                          data: ERecord({
                            tag: EEnum('div'),
                            text: EBoolean(true),
                            children: EList([]),
                            dyn: ERecord({
                              bind: ERecord({
                                innerHTML: EList([
                                  ERecord({
                                    slug: EText('productName'),
                                    type: EText('PlainText'),
                                  }),
                                ]),
                              }),
                            }),
                          }),
                        }),
                        EElement({
                          id: 'Subscription Row 1',
                          type: qualify(
                            Plugins.name('Users'),
                            Component.name('GridRow')
                          ),
                          data: ERecord({
                            children: EList([
                              EElement({
                                id: 'Subscriptions Purchased On Text',
                                type: qualify(
                                  Plugins.name('Basic'),
                                  Component.name('Block')
                                ),
                                data: ERecord({
                                  text: EBoolean(true),
                                  tag: EEnum('div'),
                                  children: EList([
                                    EElement({
                                      id: 'Subscriptions Purchased On String',
                                      type: qualify(
                                        Plugins.name('Basic'),
                                        Component.name('String')
                                      ),
                                      data: EText('Purchased on: '),
                                    }),
                                  ]),
                                }),
                              }),
                              EElement({
                                id: 'Subscription Purchased On Date',
                                type: qualify(
                                  Plugins.name('Basic'),
                                  Component.name('Block')
                                ),
                                data: ERecord({
                                  text: EBoolean(true),
                                  tag: EEnum('div'),
                                  children: EList([]),
                                  dyn: ERecord({
                                    bind: ERecord({
                                      innerHTML: EList([
                                        ERecord({
                                          slug: EText('subCreatedOn'),
                                          type: EText('Date'),
                                          filter: EText('date|M/D/YYYY'),
                                        }),
                                      ]),
                                    }),
                                  }),
                                }),
                              }),
                            ]),
                          }),
                        }),
                        EElement({
                          id: 'Subscription Row 2',
                          type: qualify(
                            Plugins.name('Users'),
                            Component.name('GridRow')
                          ),
                          data: ERecord({
                            dyn: ERecord({
                              condition: ERecord({
                                class: ERecord({
                                  false: ERecord({
                                    'w-condition-invisible': ERecord({
                                      fields: ERecord({
                                        status: ERecord({
                                          eq: EText('active'),
                                        }),
                                      }),
                                    }),
                                  }),
                                }),
                              }),
                            }),
                            children: EList([
                              EElement({
                                id: 'Subscriptions Next Billing Text',
                                type: qualify(
                                  Plugins.name('Basic'),
                                  Component.name('Block')
                                ),
                                data: ERecord({
                                  text: EBoolean(true),
                                  tag: EEnum('div'),
                                  children: EList([
                                    EElement({
                                      id: 'Subscriptions Next Billing String',
                                      type: qualify(
                                        Plugins.name('Basic'),
                                        Component.name('String')
                                      ),
                                      data: EText('Next billing: '),
                                    }),
                                  ]),
                                }),
                              }),
                              EElement({
                                id: 'Subscription Next Billing Date',
                                type: qualify(
                                  Plugins.name('Basic'),
                                  Component.name('Block')
                                ),
                                data: ERecord({
                                  text: EBoolean(true),
                                  tag: EEnum('div'),
                                  children: EList([]),
                                  dyn: ERecord({
                                    bind: ERecord({
                                      innerHTML: EList([
                                        ERecord({
                                          slug: EText('periodEnd'),
                                          type: EText('Date'),
                                          filter: EText('date|M/D/YYYY'),
                                        }),
                                      ]),
                                    }),
                                  }),
                                }),
                              }),
                            ]),
                          }),
                        }),
                        EElement({
                          id: 'Subscription Row 3',
                          type: qualify(
                            Plugins.name('Users'),
                            Component.name('GridRow')
                          ),
                          data: ERecord({
                            dyn: ERecord({
                              condition: ERecord({
                                class: ERecord({
                                  false: ERecord({
                                    'w-condition-invisible': ERecord({
                                      fields: ERecord({
                                        status: ERecord({
                                          eq: EText('canceled'),
                                        }),
                                      }),
                                    }),
                                  }),
                                }),
                              }),
                            }),
                            children: EList([
                              EElement({
                                id: 'Subscriptions Canceled On Text',
                                type: qualify(
                                  Plugins.name('Basic'),
                                  Component.name('Block')
                                ),
                                data: ERecord({
                                  text: EBoolean(true),
                                  tag: EEnum('div'),
                                  children: EList([
                                    EElement({
                                      id: 'Subscriptions Canceled On String',
                                      type: qualify(
                                        Plugins.name('Basic'),
                                        Component.name('String')
                                      ),
                                      data: EText('Canceled on: '),
                                    }),
                                  ]),
                                }),
                              }),
                              EElement({
                                id: 'Subscriptions Canceled On Date',
                                type: qualify(
                                  Plugins.name('Basic'),
                                  Component.name('Block')
                                ),
                                data: ERecord({
                                  text: EBoolean(true),
                                  tag: EEnum('div'),
                                  children: EList([]),
                                  dyn: ERecord({
                                    bind: ERecord({
                                      innerHTML: EList([
                                        ERecord({
                                          slug: EText('canceledOn'),
                                          type: EText('Date'),
                                          filter: EText('date|M/D/YYYY'),
                                        }),
                                      ]),
                                    }),
                                  }),
                                }),
                              }),
                            ]),
                          }),
                        }),
                      ]),
                    }),
                  }),
                  EElement({
                    id: 'Subscription Item Column 2',
                    type: qualify(
                      Plugins.name('Users'),
                      Component.name('FlexColumn')
                    ),
                    data: ERecord({
                      children: EList([
                        EElement({
                          id: 'Subscription Price',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('Block')
                          ),
                          data: ERecord({
                            text: EBoolean(true),
                            tag: EEnum('div'),
                            children: EList([]),
                            dyn: ERecord({
                              bind: ERecord({
                                innerHTML: EList([
                                  ERecord({
                                    slug: EText('variantPrice'),
                                    type: EText('CommercePrice'),
                                  }),
                                ]),
                              }),
                            }),
                          }),
                        }),
                        EElement({
                          id: 'Subscription Cancel',
                          type: qualify(
                            Plugins.name('Users'),
                            Component.name(
                              'UserAccountSubscriptionCancelButton'
                            )
                          ),
                          data: ERecord({
                            buttonText: EText('Cancel'),
                            dyn: ERecord({
                              condition: ERecord({
                                class: ERecord({
                                  false: ERecord({
                                    'w-condition-invisible': ERecord({
                                      fields: ERecord({
                                        status: ERecord({
                                          eq: EText('active'),
                                        }),
                                      }),
                                    }),
                                  }),
                                }),
                              }),
                            }),
                          }),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'user-account-subscription-list-empty-state',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserAccountSubscriptionListEmpty')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'empty-state-content',
              type: qualify(Plugins.name('Basic'), Component.name('Block')),
              data: ERecord({
                tag: EEnum('div'),
                text: EBoolean(true),
                children: EList([
                  EElement({
                    id: 'empty-state-text',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('No subscriptions'),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    ]),
  }),
});
